import { utils } from "@hh/global.js"

const { debounce } = utils

class HHFiltersWrapper extends HHBaseElement {
  constructor() {
    super()
    this.filterData = []
    this.filtersPanel = this.querySelector(".js-collection-filters-panel")
    this.filtersOverlay = this.querySelector(".js-collection-filters-overlay")

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)
    this.closeDetails = this.closeDetails.bind(this)
    this.onHistoryChange = this.onHistoryChange.bind(this)
    this.closeFilters = this.closeFilters.bind(this)
    this.openFilters = this.openFilters.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 500).bind(this)
  }

  connectedCallback() {
    const submitInputs = this.querySelectorAll(".js-filter-submit")
    const sortInput = this.querySelector(".js-sort-input")
    const closeButtons = this.querySelectorAll("[data-close]")

    this.bindActiveFacetButtonEvents()
    window.addEventListener("popstate", this.onHistoryChange)
    document.body.addEventListener("click", this.closeDetails)
    submitInputs.forEach((element) => {
      element.addEventListener("click", this.debouncedOnSubmit)
    })
    sortInput && sortInput.addEventListener("change", this.debouncedOnSubmit)
    closeButtons.forEach((item) => {
      item.addEventListener("click", this.closeFilters)
    })
    window.addEventListener("openFilters", this.openFilters)
  }

  get ctx() {
    return this.getAttribute("ctx") || "collection"
  }

  openFilters() {
    this.filtersOverlay?.classList.add("opened")
    this.filtersPanel?.classList.add("opened")
    document.documentElement.classList.add("overflow-hidden")
    document.body.classList.add("overflow-hidden")
    setTimeout(() => {
      document.querySelector(".js-collection-filters-close")?.focus()
      this._setupFocusTrap()
    }, 250)
    window.addEventListener("keyup", this.onKeyUp)
  }

  closeFilters() {
    this.filtersPanel?.classList.remove("opened")
    this.filtersOverlay?.classList.remove("opened")
    document.documentElement.classList.remove("overflow-hidden")
    document.body.classList.remove("overflow-hidden")
    window.removeEventListener("keyup", this.onKeyUp)
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return

    this.closeFilters()
  }

  closeDetails(event) {
    const detailsElements = this.querySelectorAll(".js-filter")
    detailsElements.forEach((detailsElement) => {
      if (!detailsElement.contains(event.target)) {
        detailsElement.open = false
      }
    })
  }

  onSubmitHandler(event) {
    event.preventDefault()
    const formData = new FormData(event.target.closest("form"))
    const searchParams = new URLSearchParams(formData).toString()
    this.renderPage(searchParams, event)
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    this.toggleActiveFacets()
    this.renderPage(new URL(event.currentTarget.href).searchParams.toString())
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || ""
    this.renderPage(searchParams, null, false)
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable)
    })
  }

  renderPage(searchParams, event, updateURLHash = true) {
    document
      .getElementById("CollectionProductGrid")
      .querySelector(".js-product-grid")
      .classList.add("loading")

    let url_banner, url_grid, filterDataUrl
    switch (this.ctx) {
      case "collection":
        url_banner =
          `${window.location.pathname}?` +
          `sections=${this.getSections()[0].section}` +
          `&${searchParams}`

        filterDataUrl = (element) => element.url === url_banner

        this.filterData.some(filterDataUrl)
          ? this.renderSectionFromCache(filterDataUrl, event, "banner")
          : this.renderSectionFromFetch(url_banner, event, "banner")

        url_grid =
          `${window.location.pathname}?` +
          `sections=${this.getSections()[1].section}` +
          `&${searchParams}`
        filterDataUrl = (element) => element.url === url_grid
        this.filterData.some(filterDataUrl)
          ? this.renderSectionFromCache(filterDataUrl, event, "grid")
          : this.renderSectionFromFetch(url_grid, event, "grid")
        break
      case "search":
        url_grid = `${window.location.pathname}?` + `${searchParams}`
        filterDataUrl = (element) => element.url === url_grid
        this.filterData.some(filterDataUrl)
          ? this.renderSectionFromCache(filterDataUrl, event, "search")
          : this.renderSearchFromFetch(url_grid, event, "search")
        break
    }

    if (updateURLHash) this.updateURLHash(searchParams)
  }

  renderSectionFromFetch(url, event, position) {
    fetch(url)
      .then((response) => response.json())
      .then((parsed) => {
        const html = Object.values(parsed).join("")
        this.filterData = [...this.filterData, { html, url }]
        switch (position) {
          case "banner":
            this.renderFilters(html, event)
            break
          case "grid":
            this.renderProductGrid(html)
            break
        }
      })
  }

  renderSearchFromFetch(url, event, position) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        this.filterData = [...this.filterData, { html, url }]

        this.renderFilters(html, event)
        this.renderProductGrid(html)
      })
  }

  renderSectionFromCache(filterDataUrl, event, position) {
    const html = this.filterData.find(filterDataUrl).html
    switch (position) {
      case "banner":
        this.renderFilters(html, event)
        break
      case "grid":
        this.renderProductGrid(html)
        break
      case "search":
        this.renderFilters(html, event)
        this.renderProductGrid(html)
        break
    }
  }

  renderProductGrid(html) {
    const productGrid = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("CollectionProductGrid")
    document.getElementById("CollectionProductGrid").replaceWith(productGrid)
  }

  renderFilters(...args) {
    this.renderSingleFormFilters("#CollectionFiltersForm", ...args)
  }

  renderSingleFormFilters(form, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html")

    const facetDetailsElements = parsedHTML.querySelectorAll(
      `${form} .js-filter`
    )
    const matchesIndex = (element) =>
      element.dataset.index ===
      event?.target.closest(".js-filter")?.dataset.index

    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    )

    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex)

    facetsToRender.forEach((element) => {
      document.querySelector(
        `${form} .js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML
    })

    this.renderActiveFacets(parsedHTML)

    if (countsToRender) {
      this.renderCounts(countsToRender, event.target.closest(".js-filter"))
    }

    this.renderItem(parsedHTML, ".js-product-total-counts")
    this.renderItem(parsedHTML, `${form} .js-count-bubble`)
  }

  renderItem(html, selector) {
    const sourceBubbleCounts = html.querySelector(`${selector}`)
    const bubbleCounts =
      this.querySelector(`${selector}`) || document.querySelector(`${selector}`)

    if (sourceBubbleCounts && bubbleCounts) {
      bubbleCounts.outerHTML = sourceBubbleCounts.outerHTML
    }
  }

  renderActiveFacets(html) {
    const activeFacetElementSelectors = [".js-facets--desktop"]

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector)
      if (!activeFacetsElement) return
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML
    })

    this.bindActiveFacetButtonEvents()
    this.toggleActiveFacets(false)
  }

  renderCounts(source, target) {
    const countElementSelectors = [".js-bubble", ".js-selected"]
    countElementSelectors.forEach((selector) => {
      const targetElement = target.querySelector(selector)
      const sourceElement = source.querySelector(selector)

      if (sourceElement && targetElement) {
        target.querySelector(selector).outerHTML =
          source.querySelector(selector).outerHTML
      }
    })
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.addEventListener("click", this.onActiveFilterClick, {
        once: true,
      })
    })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    )
  }

  getSections() {
    if (this.ctx === "collection") {
      return [
        {
          id: "main-collection-banner",
          section: document.getElementById("main-collection-banner").dataset.id,
        },
        {
          id: "main-collection-product-grid",
          section: document.getElementById("main-collection-product-grid")
            .dataset.id,
        },
      ]
    } else {
      return [
        {
          id: "main-collection-product-grid",
          section: document.getElementById("main-collection-product-grid")
            .dataset.id,
        },
      ]
    }
  }
}

customElements.define("hh-filters-wrapper", HHFiltersWrapper)
